import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

@Component({
  selector: 'app-page-data-panel',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="page-data-panel">
      <div class="panel-header">
        <div>
          <h2>{{ title }}</h2>
          @if (subtitle) {
            <p>{{ subtitle }}</p>
          }
        </div>
        <div class="panel-actions">
          @if (resultText) {
            <span class="result-chip">{{ resultText }}</span>
          }
          @if (collapsible) {
            <button type="button" class="collapse-toggle" (click)="toggleCollapsed()">
              <i class="pi" [class.pi-chevron-down]="collapsed" [class.pi-chevron-up]="!collapsed"></i>
              <span>{{ collapsed ? 'Show filters' : 'Hide filters' }}</span>
            </button>
          }
          <ng-content select="[actions]"></ng-content>
        </div>
      </div>
      @if (!collapsible || !collapsed) {
        <div class="panel-content">
          <ng-content></ng-content>
        </div>
      }
    </section>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
      min-width: 0;
      /* Keep natural height in grids while retaining the full flex-container width. */
      align-self: start;
    }

    :host([hidden]) {
      display: none !important;
    }

    .page-data-panel {
      min-width: 0;
      padding: 1rem;
      border: 1px solid var(--app-border, #dfe7f3);
      border-radius: 8px;
      background: var(--app-surface, #ffffff);
      box-shadow: 0 10px 24px var(--app-shadow-soft, rgba(15, 23, 42, 0.05));
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 0.75rem;
    }

    h2 {
      color: var(--app-text, #172033);
      font-size: 1.2rem;
      margin: 0;
    }

    p {
      color: var(--app-muted, #64748b);
      margin: 0.35rem 0 0;
    }

    .panel-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    .panel-content {
      min-width: 0;
    }

    .result-chip {
      display: inline-flex;
      align-items: center;
      width: max-content;
      max-width: 100%;
      color: var(--app-info-strong, #075985);
      background: var(--app-info-surface, #e0f2fe);
      border-radius: 6px;
      font-weight: 700;
      line-height: 1;
      padding: 0.55rem 0.7rem;
      white-space: nowrap;
    }

    .collapse-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      border: 1px solid var(--app-border, #dfe7f3);
      border-radius: 6px;
      background: transparent;
      color: var(--app-text, #172033);
      cursor: pointer;
      font: inherit;
      font-weight: 600;
      line-height: 1;
      padding: 0.55rem 0.7rem;
    }

    .collapse-toggle:hover {
      background: var(--app-surface-soft, #f8fafc);
    }

    @media (max-width: 960px) {
      .panel-header {
        align-items: stretch;
        flex-direction: column;
      }

      .panel-actions {
        justify-content: flex-start;
      }
    }

    @media (max-width: 560px) {
      .page-data-panel {
        padding: 0.85rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageDataPanelComponent implements OnInit {
  @Input() collapsible = false;
  @Input() initiallyCollapsed = false;
  @Input() title = '';
  @Input() subtitle = '';
  @Input() resultText = '';
  collapsed = false;

  ngOnInit(): void {
    this.collapsed = this.collapsible && this.initiallyCollapsed;
  }

  toggleCollapsed(): void {
    if (!this.collapsible) {
      return;
    }
    this.collapsed = !this.collapsed;
  }
}
